import React, { useEffect, useState } from "react";
import { Tab, Nav, Col, Row, Form } from "react-bootstrap";
import FeatherIcon from "feather-icons-react";
import { Typeahead } from "react-bootstrap-typeahead";
import TinymceEditor from "components/base/TinymceEditor";
import RangeCalendar from "components/custom/RangeCalendar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
type Option = {
  option_id: string;
  value?: string;
  swatch?: string;
};

type Attribute = {
  attribute_id: string;
  attribute_code: string;
  frontend_label: string;
  is_required: string;
  input_type: string;
  options?: Option[];
};

type AttributeGroup = {
  name: string;
  icon?: string;
  display?: string;
  attributes: Attribute[];
};

type ProductTabsProps = {
  AttributeGroups: AttributeGroup[];
  formValues: { [key: string]: any };
  handleChange: (attribute_code: string, value: any) => void;
  invalidFields: string[];
  readOnly?: boolean;
  alwaysReadOnlyTabs?: string[]; // Tab names that should always be read-only
  alwaysReadOnlyFields?: string[]; // Field attribute_codes that should always be read-only
  setToastMessage?: (message: string) => void;  // ✅ Make optional
  setToastVariant?: (variant: "success" | "danger") => void;
  setShowToast?: (show: boolean) => void;
  activeKey?: string | null; // Active tab key
  onSelect?: (eventKey: string | null, e?: React.SyntheticEvent<unknown, Event>) => void; // Tab selection handler matching react-bootstrap's SelectCallback
};

const ProductTabs: React.FC<ProductTabsProps> = ({
  AttributeGroups,
  formValues,
  handleChange,
  invalidFields,
  readOnly = false,
  alwaysReadOnlyTabs = [],
  alwaysReadOnlyFields = [],
  setToastMessage,   // ✅ Accept toast functions as props
  setToastVariant,
  setShowToast,
  activeKey,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const isExpedieParWamia =
  formValues.mode_expedition?.option_id === "16500";

  useEffect(() => {
    const fromDate = formValues["special_from_date"]
      ? new Date(formValues["special_from_date"])
      : null;
    const toDate = formValues["special_to_date"]
      ? new Date(formValues["special_to_date"])
      : null;

    if (!fromDate && !toDate) {
      setDateRange([null, null]);
    } else {
      setDateRange([fromDate, toDate]);
    }
  }, [formValues.special_from_date, formValues.special_to_date]);

  const handleDateRangeChange = (dates: Date[] | undefined) => {
    if (dates && dates.length === 2) {
      handleChange("special_from_date", dates[0]);
      handleChange("special_to_date", dates[1]);
      setDateRange([dates[0], dates[1]]);
    }
  };

  return (
    <Tab.Container 
      id="product-tabs" 
      defaultActiveKey={AttributeGroups[0].name}
      activeKey={activeKey || undefined}
      onSelect={onSelect}
    >
      <Row className="g-0 border-top border-bottom">
        <Col xs={12}>
          <Nav
            variant="tabs"
            className="border-bottom fs-9 justify-content-start"
          >
            {AttributeGroups.map((AttributeGroup) => (
              <Nav.Item key={AttributeGroup.name}>
                <Nav.Link
                  eventKey={AttributeGroup.name}
                  className="cursor-pointer outline-none d-flex align-items-center"
                >
                  {AttributeGroup.icon && (
                    <FeatherIcon
                      icon={AttributeGroup.icon}
                      size={16}
                      className="me-2 nav-icons"
                    />
                  )}
                  <span>{t(AttributeGroup.name)}</span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs={12}>
          <Tab.Content className="py-3 ps-sm-4">
            {AttributeGroups.map((AttributeGroup) => (
              <Tab.Pane
                key={AttributeGroup.name}
                eventKey={AttributeGroup.name}
              >
                {AttributeGroup.attributes.map((attribute, index, array) => {
                  if (attribute.input_type === "hidden") {
                    return null;
                  }
                  if (
                    isExpedieParWamia &&
                    attribute.attribute_code === "quantity_and_stock_status"
                  ) {
                    return null;
                  }
                  const isRequired =
                    attribute.attribute_code === "special_from_date" ||
                      attribute.attribute_code === "special_to_date"
                      ? !!formValues["special_price"] // ✅ Required only if special_price exists
                      : attribute.is_required === "1";
                  const inputValue = formValues[attribute.attribute_code] || "";
                  
                  // Determine if this field should be read-only
                  const isFieldReadOnly = readOnly || 
                    alwaysReadOnlyFields.includes(attribute.attribute_code) || 
                    alwaysReadOnlyTabs.includes(AttributeGroup.name);

                  return (
                    <React.Fragment key={attribute.attribute_id}>
                      {["number", "text", "price", "weight"].includes(
                        attribute.input_type
                      ) && (
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t(attribute.frontend_label)}{" "}
                              {isRequired && (
                                <span className="text-danger">*</span>
                              )}
                            </Form.Label>
                            <Form.Control
                              type={
                                attribute.input_type === "text"
                                  ? "text"
                                  : "number"
                              }
                              placeholder={t(attribute.frontend_label)}
                              required={isRequired}
                              value={inputValue}
                              readOnly={isFieldReadOnly}
                              disabled={isFieldReadOnly}
                              onChange={(e) =>
                                !isFieldReadOnly && handleChange(
                                  attribute.attribute_code,
                                  e.target.value
                                )
                              }
                              className={
                                invalidFields.includes(attribute.attribute_code)
                                  ? "is-invalid form-control-invalid"
                                  : ""
                              }
                              style={
                                invalidFields.includes(attribute.attribute_code)
                                  ? { borderColor: '#dc3545', boxShadow: '0 0 0 0.25rem rgba(220, 53, 69, 0.25)', backgroundColor: 'rgba(255, 230, 230, 0.2)' }
                                  : {}
                              }
                            />
                            {invalidFields.includes(attribute.attribute_code) && (
                              <div className="invalid-feedback">
                                {attribute.input_type === "price" || attribute.attribute_code === "special_price"
                                  ? `Please enter a price greater than 0 for ${t(attribute.frontend_label)}`
                                  : attribute.input_type === "weight" || attribute.input_type === "number"
                                    ? `Please enter a valid number for ${t(attribute.frontend_label)}`
                                    : `${t(attribute.frontend_label)} is required`}
                              </div>
                            )}
                          </Form.Group>
                        )}
                      {attribute.input_type === "textarea" && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t(attribute.frontend_label)}{" "}
                            {isRequired && (
                              <span className="text-danger">*</span>
                            )}
                          </Form.Label>
                          <div className="tinymce-wrapper">
                            <TinymceEditor
                              key={`${attribute.attribute_code}-${isFieldReadOnly ? 'readonly' : 'editable'}`}
                              options={{
                                height: "15rem",
                                placeholder: t(attribute.frontend_label),
                                readonly: isFieldReadOnly, // Make TinyMCE readonly
                                menubar: !isFieldReadOnly, // Hide menu bar if readOnly
                                toolbar: !isFieldReadOnly ? "bold italic underline link bullist numlist" : false, // Hide toolbar in readOnly mode and add more formatting options
                                inline: false,
                                setup: (editor) => {
                                  editor.on("init", () => {
                                    if (isFieldReadOnly) {
                                      editor.getBody().setAttribute("contenteditable", "false"); // Fully disable editing
                                    } else {
                                      editor.getBody().setAttribute("contenteditable", "true");
                                      // Removed auto-focus for description fields
                                    }
                                  });
                                },
                              }}
                              value={inputValue}
                              onChange={(content) =>
                                !isFieldReadOnly && handleChange(attribute.attribute_code, content) // Prevent editing if readOnly
                              }
                            />
                            {invalidFields.includes(attribute.attribute_code) && (
                              <div className="invalid-feedback">
                                {`${t(attribute.frontend_label)} is required`}
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      )}
                      {["select", "multiselect"].includes(attribute.input_type) && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Select
                            id={attribute.attribute_id}
                            className={
                              invalidFields.includes(attribute.attribute_code) ? "is-invalid form-control-invalid" : ""
                            }
                            style={
                              invalidFields.includes(attribute.attribute_code)
                                ? { borderColor: '#dc3545', boxShadow: '0 0 0 0.25rem rgba(220, 53, 69, 0.25)', backgroundColor: 'rgba(255, 230, 230, 0.2)' }
                                : {}
                            }
                            multiple={attribute.input_type === "multiselect"} // ✅ Allow multiple selections
                            value={
                              attribute.input_type === "multiselect"
                                ? (Array.isArray(formValues[attribute.attribute_code]) 
                                  ? formValues[attribute.attribute_code].map((item: { option_id?: string } | string) => 
                                      typeof item === 'object' && item !== null ? item.option_id || '' : item) 
                                  : []) // Extract option_id for each item in array
                                : (formValues[attribute.attribute_code]?.option_id || formValues[attribute.attribute_code] || "") // Extract option_id or use direct value
                            }
                            disabled={isFieldReadOnly}
                            onChange={(e) => {
                              if (isFieldReadOnly) return;
                              
                              const selectedValue =
                                attribute.input_type === "multiselect"
                                  ? Array.from(e.target.selectedOptions, (option) => option.value)
                                  : e.target.value;

                              const isValidSelection =
                                attribute.input_type === "multiselect"
                                  ? Array.isArray(selectedValue) &&
                                  selectedValue.every((val) =>
                                    (attribute.options || []).some((option) => String(option.option_id) === String(val))
                                  )
                                  : (attribute.options || []).some((option) => String(option.option_id) === String(selectedValue));


                              if (isValidSelection || attribute.input_type === "multiselect") {
                                // For single select, convert to object format with option_id and value
                                if (attribute.input_type === "select") {
                                  const selectedOption = attribute.options?.find(
                                    opt => String(opt.option_id) === String(selectedValue)
                                  );
                                  if (selectedOption) {
                                    handleChange(attribute.attribute_code, {
                                      option_id: selectedOption.option_id,
                                      value: selectedOption.value
                                    });
                                  } else {
                                    handleChange(attribute.attribute_code, selectedValue);
                                  }
                                } else {
                                  // For multiselect, keep as array of values
                                  handleChange(attribute.attribute_code, selectedValue);
                                }
                              } else {
                                // Directly use toast.error for error message
                                toast.error(`"${selectedValue}" is not a valid option.`);
                                handleChange(attribute.attribute_code, ""); // Reset if invalid
                              }
                            }}
                          >
                          {/* Show "Select an option" only for single select and when no value is chosen */}
                          {!attribute.input_type.includes("multiselect") && (
                            <option value="" disabled={!formValues[attribute.attribute_code]}>
                              {t("Select an option")}
                            </option>
                          )}

                          {(attribute.options || []).map((option) => (
                            <option key={option.option_id} value={option.option_id}>
                              {t(option.value || "Unknown Option")}
                            </option>
                          ))}
                          </Form.Select>
                          {invalidFields.includes(attribute.attribute_code) && (
                            <div className="invalid-feedback" style={{display: 'block'}}>
                              {`Please select ${t(attribute.frontend_label)}`}
                            </div>
                          )}
                        </Form.Group>
                      )}
                      {attribute.input_type === "readonly" && (
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t(attribute.frontend_label)}{" "}
                            {attribute.is_required === "1" && <span className="text-danger">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={(() => {
                              // Get the display value for readonly field
                              if (attribute.attribute_code === "politique_de_retour") {
                                // Handle object values properly
                                if (inputValue && typeof inputValue === 'object' && inputValue.value) {
                                  return inputValue.value;
                                }
                                return inputValue || t("30 jours"); // Default value for return policy
                              }
                              
                              // Handle other readonly fields that might be objects
                              if (inputValue && typeof inputValue === 'object' && inputValue.value) {
                                return inputValue.value;
                              }
                              
                              return inputValue || t("N/A");
                            })()}
                            readOnly
                            disabled
                            style={{
                              backgroundColor: '#f8f9fa',
                              borderColor: '#dee2e6',
                              color: '#6c757d',
                              cursor: 'not-allowed'
                            }}
                          />
                          <Form.Text className="text-muted">
                            {attribute.attribute_code === "politique_de_retour" 
                              ? t("attributes.returnPolicyRemark") 
                              : t("This field is automatically set and cannot be modified")
                            }
                          </Form.Text>
                        </Form.Group>
                      )}
                      {/* Render RangeCalendar directly after special_price */}
                      {attribute.attribute_code === "special_price" && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              {t("Special Price Date Range")}
                              {formValues["special_price"] && (
                                <span className="text-danger">*</span> // ✅ Show red star if special_price exists
                              )}
                            </Form.Label>
                            <RangeCalendar
                              value={dateRange}
                              onChange={handleDateRangeChange}
                              placeholder={t("Select date range")}
                              key={`date-range-${formValues.special_from_date || ""}-${formValues.special_to_date || ""}`} // ✅ Forces re-render when cleared
                              options={{
                                mode: "range",
                                enableTime: true,
                                // Removed minDate restriction to show past dates
                                dateFormat: "Y-m-d h:i",
                              }}
                            />
                          </Form.Group>
                        </>
                      )}

                    </React.Fragment>
                  );
                })}
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Col>
      </Row>
    </Tab.Container>
  );
};

export default ProductTabs;
